import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  Grid,
  FormControl,
  FormControlLabel,
  Checkbox,
  MenuItem,
  Divider,
  Chip,
  TextField,
  FormHelperText,
} from "@mui/material";
import CloseSharpIcon from "@mui/icons-material/CloseSharp";
import { useFormik } from "formik";
import * as yup from "yup";
import {
  useGetSchoolDiscountsQuery,
  useApplyDiscountMutation,
} from "./discountApiSlice";
import { useGetCurrentTermQuery } from "../settings/session/sessionApiSlice";
import { useGetClassesPaginatedQuery } from "../classroom/classApiSlice";
import {
  useGetSectionsPaginatedQuery,
  useGetSubSectionsPaginatedQuery,
} from "../settings/sections/sectionsApiSlice";
import { useGetFeesQuery } from "../fees-schedule/feeScheduleApiSlice";
import { StyledTextField, StyledSelect } from "../../customComponents/styled/styledInputs";
import { StyledLoadingButton } from "../../customComponents/styled/styledButtons";
import { StyledMultiSelect } from "../../customComponents/styled/customMultiSelect";
import toast from "../../../helpers/toast";

const FILTER_TYPES = [
  { value: "classroom_id", label: "Classroom" },
  { value: "subsection_id", label: "Subsection" },
  { value: "section_id", label: "Section" },
  { value: "reg_numbers", label: "Registration Numbers" },
  { value: "parent_contact", label: "Parent Email/Phone" },
];

const validationSchema = yup.object({
  discount_id: yup.number().required("Select a discount"),
  filter_type: yup.string().required("Select a grouping method"),
  classroom_id: yup.number().when("filter_type", {
    is: "classroom_id",
    then: (s) => s.required("Select a classroom"),
  }),
  subsection_id: yup.number().when("filter_type", {
    is: "subsection_id",
    then: (s) => s.required("Select a subsection"),
  }),
  section_id: yup.number().when("filter_type", {
    is: "section_id",
    then: (s) => s.required("Select a section"),
  }),
  reg_numbers_text: yup.string().when("filter_type", {
    is: "reg_numbers",
    then: (s) => s.required("Enter at least one registration number"),
  }),
  parent_contact_text: yup.string().when("filter_type", {
    is: "parent_contact",
    then: (s) => s.required("Enter a parent email or phone number"),
  }),
  note: yup.string(),
});

function ApplyDiscountDialog({ open, setOpen }) {
  const [classroomSearch, setClassroomSearch] = useState("");
  const [applyDiscount, { isLoading: applying }] = useApplyDiscountMutation();

  const { data: discounts = [] } = useGetSchoolDiscountsQuery();
  const { data: currentTerm } = useGetCurrentTermQuery();
  const { data: classroomsData } = useGetClassesPaginatedQuery({
    page: 1,
    per_page: 50,
    q: classroomSearch,
  });
  const { data: sectionsData } = useGetSectionsPaginatedQuery({ page: 1, per_page: 100 });
  const { data: subsectionsData } = useGetSubSectionsPaginatedQuery({ page: 1, per_page: 100 });

  const classrooms = classroomsData?.classrooms ?? [];
  const sections = sectionsData?.sections ?? [];
  const subsections = subsectionsData?.subsections ?? [];

  const handleClose = () => {
    setOpen(false);
    formik.resetForm();
  };

  const formik = useFormik({
    initialValues: {
      discount_id: "",
      filter_type: "classroom_id",
      classroom_id: "",
      subsection_id: "",
      section_id: "",
      reg_numbers_text: "",
      parent_contact_text: "",
      scope_enabled: false,
      scope_classroom_id: "",
      fee_ids: [],
      note: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      if (!currentTerm) {
        toast.error("No active term found");
        return;
      }
      const student_filter = buildStudentFilter(values);
      if (!student_filter) return;

      const body = {
        discount_id: values.discount_id,
        session_id: currentTerm.session_id,
        term_id: currentTerm.id,
        student_filter,
        fee_ids: values.fee_ids.map((f) => f.value),
        note: values.note || undefined,
      };

      try {
        const result = await applyDiscount(body).unwrap();
        toast.success(
          `Discount applied to ${result.applied} invoice(s). ${result.skipped > 0 ? `${result.skipped} student(s) had no invoice for this term.` : ""}`
        );
        handleClose();
      } catch (err) {
        toast.error(err?.data?.message || "Failed to apply discount");
      }
    },
  });

  function buildStudentFilter(values) {
    if (values.filter_type === "classroom_id") {
      return { classroom_id: values.classroom_id };
    }
    if (values.filter_type === "subsection_id") {
      return { subsection_id: values.subsection_id };
    }
    if (values.filter_type === "section_id") {
      return { section_id: values.section_id };
    }
    if (values.filter_type === "reg_numbers") {
      const reg_numbers = values.reg_numbers_text
        .split(/[\n,]+/)
        .map((r) => r.trim())
        .filter(Boolean);
      if (reg_numbers.length === 0) {
        toast.error("Enter at least one registration number");
        return null;
      }
      return { reg_numbers };
    }
    if (values.filter_type === "parent_contact") {
      return { parent_contact: values.parent_contact_text.trim() };
    }
    return null;
  }

  const selectedDiscount = discounts.find((d) => d.id === formik.values.discount_id);

  // Item scoping is independent of how students were grouped — a discount
  // can be limited to specific fee items regardless of filter type or
  // discount type. Defaults the scope classroom to the filter's classroom
  // when that's how students were grouped, but can be changed either way.
  const effectiveScopeClassroomId =
    formik.values.scope_classroom_id ||
    (formik.values.filter_type === "classroom_id" ? formik.values.classroom_id : "");

  const { data: classroomFees } = useGetFeesQuery(
    effectiveScopeClassroomId ? `classroom/${effectiveScopeClassroomId}` : "skip",
    { skip: !formik.values.scope_enabled || !effectiveScopeClassroomId }
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1,
        }}
      >
        <Typography variant="h6">Apply Discount to Invoices</Typography>
        <IconButton size="small" onClick={handleClose}>
          <CloseSharpIcon />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent>
        {!currentTerm && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="warning.main">
              No active term detected. Discounts will use the current term from the server.
            </Typography>
          </Box>
        )}
        {currentTerm && (
          <Box sx={{ mb: 2, display: "flex", gap: 1 }}>
            <Chip label={`Term: ${currentTerm.name}`} size="small" variant="outlined" />
            {currentTerm.session?.name && (
              <Chip label={`Session: ${currentTerm.session.name}`} size="small" variant="outlined" />
            )}
          </Box>
        )}

        <Box component="form" onSubmit={formik.handleSubmit}>
          <Grid container rowSpacing={2.5}>
            {/* Discount selection */}
            <Grid item xs={12}>
              <Typography variant="formSubHeading">DISCOUNT</Typography>
              <FormControl
                fullWidth
                error={formik.touched.discount_id && Boolean(formik.errors.discount_id)}
              >
                <StyledSelect
                  name="discount_id"
                  value={formik.values.discount_id}
                  onChange={formik.handleChange}
                  displayEmpty
                >
                  <MenuItem value="" disabled>
                    Select a discount
                  </MenuItem>
                  {discounts.map((d) => (
                    <MenuItem key={d.id} value={d.id}>
                      {d.name} —{" "}
                      {d.type === "percentage"
                        ? `${d.value}% off`
                        : `₦${Number(d.value).toLocaleString()} off`}
                    </MenuItem>
                  ))}
                </StyledSelect>
                <FormHelperText>
                  {formik.touched.discount_id && formik.errors.discount_id}
                </FormHelperText>
              </FormControl>
              {discounts.length === 0 && (
                <Typography variant="caption" color="text.secondary">
                  No discounts available. Create one in "Manage Discounts" first.
                </Typography>
              )}
            </Grid>

            {/* Student grouping method */}
            <Grid item xs={12}>
              <Typography variant="formSubHeading">GROUP STUDENTS BY</Typography>
              <FormControl
                fullWidth
                error={formik.touched.filter_type && Boolean(formik.errors.filter_type)}
              >
                <StyledSelect
                  name="filter_type"
                  value={formik.values.filter_type}
                  onChange={(e) => {
                    formik.handleChange(e);
                    formik.setFieldValue("classroom_id", "");
                    formik.setFieldValue("subsection_id", "");
                    formik.setFieldValue("section_id", "");
                    formik.setFieldValue("reg_numbers_text", "");
                    formik.setFieldValue("parent_contact_text", "");
                    formik.setFieldValue("fee_ids", []);
                  }}
                >
                  {FILTER_TYPES.map((ft) => (
                    <MenuItem key={ft.value} value={ft.value}>
                      {ft.label}
                    </MenuItem>
                  ))}
                </StyledSelect>
              </FormControl>
            </Grid>

            {/* Classroom picker */}
            {formik.values.filter_type === "classroom_id" && (
              <Grid item xs={12}>
                <Typography variant="formSubHeading">CLASSROOM</Typography>
                <FormControl
                  fullWidth
                  error={formik.touched.classroom_id && Boolean(formik.errors.classroom_id)}
                >
                  <StyledSelect
                    name="classroom_id"
                    value={formik.values.classroom_id}
                    onChange={formik.handleChange}
                    displayEmpty
                  >
                    <MenuItem value="" disabled>
                      Select a classroom
                    </MenuItem>
                    {classrooms.map((c) => (
                      <MenuItem key={c.id} value={c.id}>
                        {c.name}
                      </MenuItem>
                    ))}
                  </StyledSelect>
                  <FormHelperText>
                    {formik.touched.classroom_id && formik.errors.classroom_id}
                  </FormHelperText>
                </FormControl>
              </Grid>
            )}

            {/* Subsection picker */}
            {formik.values.filter_type === "subsection_id" && (
              <Grid item xs={12}>
                <Typography variant="formSubHeading">SUBSECTION</Typography>
                <FormControl
                  fullWidth
                  error={formik.touched.subsection_id && Boolean(formik.errors.subsection_id)}
                >
                  <StyledSelect
                    name="subsection_id"
                    value={formik.values.subsection_id}
                    onChange={formik.handleChange}
                    displayEmpty
                  >
                    <MenuItem value="" disabled>
                      Select a subsection
                    </MenuItem>
                    {subsections.map((s) => (
                      <MenuItem key={s.id} value={s.id}>
                        {s.name}
                      </MenuItem>
                    ))}
                  </StyledSelect>
                  <FormHelperText>
                    {formik.touched.subsection_id && formik.errors.subsection_id}
                  </FormHelperText>
                </FormControl>
              </Grid>
            )}

            {/* Section picker */}
            {formik.values.filter_type === "section_id" && (
              <Grid item xs={12}>
                <Typography variant="formSubHeading">SECTION</Typography>
                <FormControl
                  fullWidth
                  error={formik.touched.section_id && Boolean(formik.errors.section_id)}
                >
                  <StyledSelect
                    name="section_id"
                    value={formik.values.section_id}
                    onChange={formik.handleChange}
                    displayEmpty
                  >
                    <MenuItem value="" disabled>
                      Select a section
                    </MenuItem>
                    {sections.map((s) => (
                      <MenuItem key={s.id} value={s.id}>
                        {s.name}
                      </MenuItem>
                    ))}
                  </StyledSelect>
                  <FormHelperText>
                    {formik.touched.section_id && formik.errors.section_id}
                  </FormHelperText>
                </FormControl>
              </Grid>
            )}

            {/* Reg numbers textarea */}
            {formik.values.filter_type === "reg_numbers" && (
              <Grid item xs={12}>
                <Typography variant="formSubHeading">REGISTRATION NUMBERS</Typography>
                <StyledTextField
                  fullWidth
                  name="reg_numbers_text"
                  placeholder="Enter reg numbers, one per line or comma-separated"
                  value={formik.values.reg_numbers_text}
                  onChange={formik.handleChange}
                  multiline
                  rows={4}
                  error={
                    formik.touched.reg_numbers_text &&
                    Boolean(formik.errors.reg_numbers_text)
                  }
                  helperText={
                    formik.touched.reg_numbers_text && formik.errors.reg_numbers_text
                  }
                />
              </Grid>
            )}

            {/* Reg parent contact */}
            {formik.values.filter_type === "parent_contact" && (
              <Grid item xs={12}>
                <Typography variant="formSubHeading">PARENT EMAIL OR PHONE</Typography>
                <StyledTextField
                  fullWidth
                  name="parent_contact_text"
                  placeholder="e.g. parent@email.com or 08012345678"
                  value={formik.values.parent_contact_text}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.parent_contact_text &&
                    Boolean(formik.errors.parent_contact_text)
                  }
                  helperText={
                    formik.touched.parent_contact_text && formik.errors.parent_contact_text
                  }
                />
              </Grid>
            )}

            {/* Item scoping — works for any grouping method and any discount type */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formik.values.scope_enabled}
                    onChange={(e) => {
                      formik.setFieldValue("scope_enabled", e.target.checked);
                      if (!e.target.checked) formik.setFieldValue("fee_ids", []);
                    }}
                  />
                }
                label="Only apply this discount to specific fee items (leave unchecked to discount the whole invoice)"
              />
            </Grid>

            {formik.values.scope_enabled && (
              <>
                <Grid item xs={12}>
                  <Typography variant="formSubHeading">
                    SCOPE TO FEES FROM CLASSROOM
                  </Typography>
                  <FormControl fullWidth>
                    <StyledSelect
                      value={formik.values.scope_classroom_id}
                      onChange={(e) => {
                        formik.setFieldValue("scope_classroom_id", e.target.value);
                        formik.setFieldValue("fee_ids", []);
                      }}
                      displayEmpty
                    >
                      <MenuItem value="" disabled>
                        Select a classroom
                      </MenuItem>
                      {classrooms.map((c) => (
                        <MenuItem key={c.id} value={c.id}>
                          {c.name}
                        </MenuItem>
                      ))}
                    </StyledSelect>
                  </FormControl>
                  <Typography variant="caption" color="text.secondary">
                    Students may be grouped by class/section/reg-number/parent above —
                    this only picks which fee catalog to choose items from.
                  </Typography>
                </Grid>

                {effectiveScopeClassroomId && classroomFees?.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="formSubHeading">
                      APPLY DISCOUNT TO THESE ITEMS
                    </Typography>
                    <StyledMultiSelect
                      id="fee_ids"
                      name="fee_ids"
                      value={formik.values.fee_ids}
                      onChange={(selected) => formik.setFieldValue("fee_ids", selected)}
                      options={classroomFees.map((f) => ({
                        label: `${f.name} — ₦${(f.amount || 0).toLocaleString()}`,
                        value: f.id,
                      }))}
                      placeholder="Select specific fees"
                    />
                    <Typography variant="caption" color="text.secondary">
                      {selectedDiscount?.type === "percentage"
                        ? `${selectedDiscount.value}% will be calculated off the sum of the selected items only.`
                        : `₦${Number(selectedDiscount?.value || 0).toLocaleString()} will be applied, capped at the sum of the selected items.`}
                    </Typography>
                  </Grid>
                )}
              </>
            )}

            {/* Note */}
            <Grid item xs={12}>
              <Typography variant="formSubHeading">NOTE (OPTIONAL)</Typography>
              <StyledTextField
                fullWidth
                name="note"
                placeholder="e.g. Early enrolment scholarship"
                value={formik.values.note}
                onChange={formik.handleChange}
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <StyledLoadingButton
          variant="outlined"
          onClick={handleClose}
          sx={{ textTransform: "none" }}
        >
          Cancel
        </StyledLoadingButton>
        <StyledLoadingButton
          loading={applying}
          onClick={formik.handleSubmit}
          sx={{ textTransform: "none" }}
          disabled={discounts.length === 0}
        >
          Apply Discount
        </StyledLoadingButton>
      </DialogActions>
    </Dialog>
  );
}

export default ApplyDiscountDialog;
